/**
 * Services Business Logic Hooks
 *
 * TanStack Query hooks for service management (service subcategories, etc.)
 * Optimized for caching with moderate refresh intervals
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { generateInvoiceNumberApi, getAppointmentForInvoiceApi, getAppointmentForInvoiceByCustomerApi, invoiceListApi } from './invoices.api';

// Query Keys for invoices domain
export const businessInvoicesKeys = {
    all: ['invoices'],
    generateInvoiceNumber: () => [...businessInvoicesKeys.all, 'generateInvoiceNumber'],
    appointmentForInvoice: (params) => [...businessInvoicesKeys.all, 'appointmentForInvoice', params],
    appointmentForInvoiceByCustomer: (params) => [...businessInvoicesKeys.all, 'appointmentForInvoiceByCustomer', params],
    invoicesList: (params) => [...businessInvoicesKeys.all, 'list', params],
    invoicesInfiniteList: (params) => [...businessInvoicesKeys.all, 'infiniteList', params],
};

export const useGenerateInvoiceNumber = (id, options = {}) => {
    return useQuery({
        queryKey: businessInvoicesKeys.generateInvoiceNumber(),
        queryFn: () => generateInvoiceNumberApi(),
        enabled: !id ? true : false,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...options,
    });
};

export const useGetAppointmentForInvoice = (params, options = {}) => {
    return useQuery({
        queryKey: businessInvoicesKeys.appointmentForInvoice(params),
        queryFn: () => getAppointmentForInvoiceApi(params),
        enabled: !!params?.customerId && !!params?.vehicleId && !!params?.shopId,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...options,
    });
};

export const useGetAppointmentForInvoiceByCustomer = (params, options = {}) => {
    return useQuery({
        queryKey: businessInvoicesKeys.appointmentForInvoiceByCustomer(params),
        queryFn: () => getAppointmentForInvoiceByCustomerApi(params),
        enabled: !!params?.customerId && !!params?.shopId,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        ...options,
    });
};

export const useInvoiceList = (params, options = {}) => {
    return useQuery({
        queryKey: businessInvoicesKeys.invoicesList(params),
        queryFn: () => invoiceListApi(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
};

/**
 * Hook to get infinite invoice list with search and pagination
 *
 * Uses infinite query for optimized loading and caching:
 * - Infinite scroll pagination
 * - Smart search debouncing
 * - Optimized caching with search parameters
 * - Background refetch for better UX
 * - Automatic cache invalidation on invoice changes
 *
 * @param {object} params - Query parameters
 * @param {number} params.limit - Number of items per page (default: 20)
 * @param {string} params.status - Invoice status filter
 * @param {string} params.sortType - Sort order (ASC/DSC)
 * @param {string} params.sortBy - Sort field
 * @param {string} params.timezone - Timezone for date formatting
 * @param {string} params.customer - Customer ID filter
 * @param {string} params.customerSearch - Customer search term
 * @param {object} options - Query options
 * @returns {object} Infinite query result with invoice data
 */
export const useInfiniteInvoiceList = (params = {}, options = {}) => {
    // Set default limit if not provided
    const queryParams = {
        limit: 20,
        ...params,
    };

    return useInfiniteQuery({
        queryKey: businessInvoicesKeys.invoicesInfiniteList(queryParams),
        queryFn: ({ pageParam = 1 }) => {
            return invoiceListApi({
                ...queryParams,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) => {
            // Handle different API response structures
            let lastPageData = [];
            let hasMore = false;

            // If lastPage is an array directly (most likely case based on current usage)
            if (Array.isArray(lastPage)) {
                lastPageData = lastPage;
                // If the last page has items equal to the limit, there might be more pages
                hasMore = lastPage.length === queryParams.limit;
            }
            // If API returns an object with data array and pagination info
            else if (lastPage && typeof lastPage === 'object') {
                // Check for pagination metadata
                if (lastPage.totalPages !== undefined) {
                    hasMore = allPages.length < lastPage.totalPages;
                } else if (lastPage.hasMore !== undefined) {
                    hasMore = lastPage.hasMore;
                } else if (lastPage.data && Array.isArray(lastPage.data)) {
                    lastPageData = lastPage.data;
                    hasMore = lastPageData.length === queryParams.limit;
                } else if (Array.isArray(lastPage.invoices)) {
                    lastPageData = lastPage.invoices;
                    hasMore = lastPageData.length === queryParams.limit;
                }
            }

            return hasMore ? allPages.length + 1 : undefined;
        },
        getPreviousPageParam: (firstPage, allPages) => {
            // For previous page support (optional)
            if (allPages.length > 1) {
                return allPages.length - 1;
            }
            return undefined;
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false, // Prevent refetch on window focus for better performance
        ...options,
    });
};
